import * as React from "react";
import {
  parseLocalDate,
  Results,
  type ResultsSearchParams,
  type VehicleResult,
} from "@fm/ui";
import { inventory, categories } from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import type { CategoryWithDetails } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";
import { buildVehicleBookingModeMap } from "../lib/vehicleBookingMode";
import { SearchBarContainer } from "./SearchBarContainer";
import type { SearchBarFormData } from "@fm/ui";

export interface ResultsContainerProps {
  apiKey: string;
  fleetId: string;
  searchParams: ResultsSearchParams;
  apiBaseUrl?: string;
  theme?: "light" | "dark";
  className?: string;
  primaryColor?: string;
  showSearchBar?: boolean;
  onVehicleSelect?: (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
  }) => void;
  onReserve?: (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
    booking_id?: string;
    quote_id?: string;
  }) => void;
  onBackToSearch?: () => void;
  onSearchBarSearch?: (searchParams: ResultsSearchParams) => void;
  getVehicleHref?: (vehicle: VehicleResult) => string;
  showBackButton?: boolean;
  currency?: string;
  showReserveButton?: boolean;
}

export function ResultsContainer({
  apiKey,
  fleetId,
  searchParams: initialSearchParams,
  apiBaseUrl,
  theme = "light",
  className,
  primaryColor,
  showSearchBar = false,
  onVehicleSelect,
  onReserve,
  onBackToSearch,
  onSearchBarSearch,
  getVehicleHref,
  showBackButton = true,
  currency = "USD",
  showReserveButton = false,
}: ResultsContainerProps) {
  const [vehicles, setVehicles] = React.useState<VehicleResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = React.useState<string | null>(
    null,
  );
  const [searchParams, setSearchParams] =
    React.useState<ResultsSearchParams>(initialSearchParams);
  const [categoriesData, setCategoriesData] = React.useState<
    CategoryWithDetails[]
  >([]);
  const [categoriesLoaded, setCategoriesLoaded] = React.useState(false);

  // Create API client
  const apiClient = React.useMemo<ApiClient>(
    () => ({
      apiKey,
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  React.useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesResponse = await categories.list(apiClient, fleetId);
        const categoriesListData = categoriesResponse.data;

        const categoriesWithDetails = await Promise.all(
          categoriesListData.map(async (category) => {
            try {
              const detailResponse = await categories.get(
                apiClient,
                fleetId,
                category.id,
              );
              return detailResponse.data;
            } catch {
              return {
                ...category,
                vehicles: [],
                photos: [],
              } as CategoryWithDetails;
            }
          }),
        );

        setCategoriesData(categoriesWithDetails);
      } catch (error) {
        console.error("Failed to load categories:", error);
      } finally {
        setCategoriesLoaded(true);
      }
    };

    loadCategories();
  }, [apiClient, fleetId]);

  // Build a map of category_id -> Set of vehicle IDs
  const categoryVehicleMap = React.useMemo(() => {
    const map = new Map<string, Set<string>>();

    categoriesData.forEach((category) => {
      const vehicleIds = new Set((category.vehicles || []).map((v) => v.id));
      map.set(category.id, vehicleIds);
    });

    return map;
  }, [categoriesData]);

  // The inventory grid does not carry `booking_mode`, but every category's
  // detail lists its vehicles with their EFFECTIVE mode.
  const vehicleBookingModes = React.useMemo(
    () => buildVehicleBookingModeMap(categoriesData),
    [categoriesData],
  );

  // Load inventory data
  React.useEffect(() => {
    if (!categoriesLoaded) {
      return;
    }

    const loadInventory = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get inventory grid for the date range
        const inventoryResponse = await inventory.getGrid(apiClient, {
          from_date: searchParams.from,
          to_date: searchParams.to,
        });

        if (!inventoryResponse.data) {
          throw new Error("No inventory data received");
        }

        // Process vehicles and calculate pricing
        const processedVehicles: VehicleResult[] =
          inventoryResponse.data.vehicles
            .map((vehicle) => {
              // Check if vehicle is available for all dates in the range
              const isAvailable = vehicle.daily_inventory.every(
                (day) => day.available,
              );

              if (!isAvailable) {
                return null; // Filter out unavailable vehicles
              }

              // Check if vehicle has valid rates for all dates (no null rates)
              const hasValidRates = vehicle.daily_inventory.every(
                (day) => day.rate !== null,
              );

              if (!hasValidRates) {
                return null; // Filter out vehicles without pricing
              }

              // Calculate total price and average rate
              // Only include days from pick_up (inclusive) to return (exclusive)
              const totalPrice = vehicle.daily_inventory
                .filter((day) => {
                  const dayDate = parseLocalDate(day.date);
                  const pickUpDate = parseLocalDate(searchParams.from);
                  const returnDate = parseLocalDate(searchParams.to);

                  // Include day if it's >= pick_up AND < return
                  return dayDate >= pickUpDate && dayDate < returnDate;
                })
                .reduce((sum, day) => sum + (day.rate ?? 0), 0);

              // Calculate average rate based on actual included days
              const includedDays = vehicle.daily_inventory.filter((day) => {
                const dayDate = parseLocalDate(day.date);
                const pickUpDate = parseLocalDate(searchParams.from);
                const returnDate = parseLocalDate(searchParams.to);

                return dayDate >= pickUpDate && dayDate < returnDate;
              });

              const averageRate =
                includedDays.length > 0 ? totalPrice / includedDays.length : 0;

              return {
                id: vehicle.id,
                name: vehicle.name,
                year: vehicle.year,
                category: vehicle.category,
                equipment_type: vehicle.equipment_type,
                vehicle_type: vehicle.vehicle_type,
                sleeps: vehicle.sleeps,
                length_feet: vehicle.length_feet,
                photos: vehicle.photos,
                daily_inventory: vehicle.daily_inventory,
                totalPrice,
                averageRate,
                booking_mode: vehicleBookingModes.get(vehicle.id),
              };
            })
            .filter(Boolean) as VehicleResult[];

        // Apply filters if specified
        let filteredVehicles = processedVehicles;

        if (searchParams.category_id) {
          // Get the set of vehicle IDs that belong to this category
          const vehicleIdsInCategory = categoryVehicleMap.get(
            searchParams.category_id,
          );

          if (vehicleIdsInCategory) {
            filteredVehicles = filteredVehicles.filter((v) =>
              vehicleIdsInCategory.has(v.id),
            );
          }
        }

        if (searchParams.vehicle_id) {
          filteredVehicles = filteredVehicles.filter(
            (v) => v.id === searchParams.vehicle_id,
          );
        }

        setVehicles(filteredVehicles);
      } catch (error) {
        console.error("Failed to load inventory:", error);
        setError("Failed to load available vehicles. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadInventory();
  }, [
    apiClient,
    searchParams,
    categoryVehicleMap,
    categoriesLoaded,
    vehicleBookingModes,
  ]);

  const handleVehicleSelect = async (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
  }) => {
    try {
      setSelectedVehicle(vehicleData.vehicle_id);

      // Call onVehicleSelect callback
      onVehicleSelect?.(vehicleData);
    } catch (error) {
      console.error("Failed to select vehicle:", error);
    } finally {
      setSelectedVehicle(null);
    }
  };

  const handleReserve = (vehicleData: {
    vehicle_id: string;
    vehicle_name: string;
    total_price: number;
    search_params: ResultsSearchParams;
  }) => {
    // Instead of creating the reservation immediately,
    // redirect to checkout with the selected vehicle data
    onReserve?.({
      vehicle_id: vehicleData.vehicle_id,
      vehicle_name: vehicleData.vehicle_name,
      total_price: vehicleData.total_price,
      search_params: vehicleData.search_params,
      // These will be filled in during checkout
      booking_id: undefined,
      quote_id: undefined,
    });
  };

  const handleSearch = (data: SearchBarFormData & { fleet_id?: string }) => {
    const newSearchParams: ResultsSearchParams = {
      from: data.pickUp,
      to: data.return,
      adults: data.adults,
      children: data.children,
      infants: data.infants,
      pets: data.pets,
      category_id: data.categoryId,
      vehicle_id: data.vehicleId,
    };

    // Update local state
    setSearchParams(newSearchParams);

    // Notify parent component to update URL
    onSearchBarSearch?.(newSearchParams);
  };

  return (
    <div className={className}>
      {showSearchBar && (
        <div style={{ marginBottom: "36px" }}>
          <SearchBarContainer
            apiKey={apiKey}
            fleetId={fleetId}
            apiBaseUrl={apiBaseUrl}
            theme={theme}
            primaryColor={primaryColor}
            onSearch={handleSearch}
            initialData={{
              pickUp: searchParams.from,
              return: searchParams.to,
              adults: searchParams.adults,
              children: searchParams.children,
              infants: searchParams.infants,
              pets: searchParams.pets,
              categoryId: searchParams.category_id,
              vehicleId: searchParams.vehicle_id,
            }}
          />
        </div>
      )}
      <Results
        vehicles={vehicles}
        searchParams={searchParams}
        isLoading={isLoading}
        error={error}
        selectedVehicle={selectedVehicle}
        theme={theme}
        onVehicleSelect={handleVehicleSelect}
        onReserve={handleReserve}
        onBackToSearch={onBackToSearch}
        getVehicleHref={getVehicleHref}
        showBackButton={showBackButton}
        currency={currency}
        showReserveButton={showReserveButton}
      />
    </div>
  );
}
